import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { isAdmin } from '../../common/actionHelper';
import { NoraButton } from '../../common/components/v2';
import { NoraPermissionV2 } from '../../common/constants/roles';
import { FetchService } from '../../common/fetch.service';
import { useAccess } from '../../common/hooks/useAccess';
import styles from './styles.module.scss';
import { RbacRolePermissionsResponse } from './types';

function formatToken(token: string): string {
  return token.charAt(0) + token.slice(1).toLowerCase();
}

function formatRoleLabel(role: string): string {
  return role
    .replace(/^WORKBENCH_/, '')
    .split('_')
    .map(formatToken)
    .join(' ');
}

function formatPermissionLabel(permission: string): string {
  return permission.split('_').map(formatToken).join(' ');
}

function areSamePermissions(left: string[], right: string[]): boolean {
  const normalizedLeft = [...left].sort();
  const normalizedRight = [...right].sort();
  if (normalizedLeft.length !== normalizedRight.length) return false;
  return normalizedLeft.every((p, i) => p === normalizedRight[i]);
}

export function RbacManagerPage() {
  const queryClient = useQueryClient();
  const canViewControlPanel = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: isAdmin,
  });

  const [draftState, setDraftState] = useState<Record<string, string[]>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { data, isLoading, isError, refetch } =
    useQuery<RbacRolePermissionsResponse>({
      queryKey: ['rbac-role-permissions'],
      queryFn: () => FetchService.getInstance().get('/rbac/role-permissions'),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  useEffect(() => {
    if (!data?.rolePermissions) return;
    const initial: Record<string, string[]> = {};
    data.rolePermissions.forEach(({ role, permissions }) => {
      initial[role] = permissions;
    });
    setDraftState(initial);
  }, [data?.rolePermissions]);

  const dirtyRoles = useMemo(() => {
    if (!data?.rolePermissions) return new Set<string>();
    const dirty = new Set<string>();
    data.rolePermissions.forEach(({ role, permissions }) => {
      if (!areSamePermissions(draftState[role] || [], permissions)) {
        dirty.add(role);
      }
    });
    return dirty;
  }, [draftState, data?.rolePermissions]);

  const visiblePermissions = useMemo(() => {
    const permissions = data?.availablePermissions || [];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) return permissions;

    return permissions.filter(
      permission =>
        permission.toLowerCase().includes(normalizedSearch) ||
        formatPermissionLabel(permission)
          .toLowerCase()
          .includes(normalizedSearch),
    );
  }, [data?.availablePermissions, searchTerm]);

  const { mutate: saveAll, isLoading: isSaving } = useMutation({
    mutationFn: () =>
      Promise.all(
        [...dirtyRoles].map(role =>
          FetchService.getInstance().put(`/rbac/role-permissions/${role}`, {
            body: { permissions: [...(draftState[role] || [])].sort() },
          }),
        ),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['rbac-role-permissions'],
      });
      setSnackbar({
        severity: 'success',
        message: `Saved ${dirtyRoles.size} role${dirtyRoles.size === 1 ? '' : 's'}.`,
      });
      setSnackbarOpen(true);
    },
    onError: (error: Error) => {
      setSnackbar({
        severity: 'error',
        message: error?.message || 'Failed to save role permissions.',
      });
      setSnackbarOpen(true);
    },
  });

  const resetAll = () => {
    if (!data?.rolePermissions) return;
    const reset: Record<string, string[]> = {};
    data.rolePermissions.forEach(({ role, permissions }) => {
      reset[role] = permissions;
    });
    setDraftState(reset);
  };

  const togglePermission = (role: string, permission: string) => {
    setDraftState(current => {
      const rolePerms = current[role] || [];
      const updated = rolePerms.includes(permission)
        ? rolePerms.filter(p => p !== permission)
        : [...rolePerms, permission];
      return { ...current, [role]: updated };
    });
  };

  if (!canViewControlPanel) {
    return (
      <Box className={styles.page}>
        <Paper sx={{ padding: 4 }}>
          <Typography variant="h1">
            You are not authorized to access this page
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className={styles.page}>
        <Paper
          sx={{ minHeight: 360, display: 'grid', placeItems: 'center', gap: 2 }}
        >
          <CircularProgress />
          <Typography variant="h1">Loading RBAC manager...</Typography>
        </Paper>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box className={styles.page}>
        <Paper
          sx={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Typography variant="h1">Unable to load RBAC data</Typography>
          <Typography variant="body2" color="text.secondary">
            Check that the backend is running locally and your token has access
            to the RBAC admin endpoints.
          </Typography>
          <Box>
            <NoraButton variant="primary" onClick={() => refetch()}>
              Retry
            </NoraButton>
          </Box>
        </Paper>
      </Box>
    );
  }

  const roles = data.rolePermissions;

  return (
    <Box className={styles.page}>
      <Box className={styles.matrixCard}>
        <Box className={styles.cardToolbar}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
              Role Permissions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {roles.length} roles &middot; {data.availablePermissions.length} permissions
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            placeholder="Search…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{ width: 200 }}
          />
          {dirtyRoles.size > 0 && (
            <span className={styles.dirtyBadge}>
              {dirtyRoles.size} unsaved
            </span>
          )}
          <NoraButton
            variant="grayOutline"
            onClick={resetAll}
            disabled={dirtyRoles.size === 0 || isSaving}
          >
            Reset
          </NoraButton>
          <NoraButton
            variant="infoSolid"
            onClick={() => saveAll()}
            disabled={dirtyRoles.size === 0 || isSaving}
            isLoading={isSaving}
          >
            Save
          </NoraButton>
        </Box>

        <Box className={styles.tableScrollArea}>
        {visiblePermissions.length === 0 ? (
          <Box className={styles.emptyState}>
            <Typography variant="h6">
              No permissions match
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
              Try a different search term.
            </Typography>
          </Box>
        ) : (
          <table className={styles.matrixTable}>
            <thead>
              <tr>
                <th className={styles.permissionHeaderCell}>Permission</th>
                {roles.map(({ role }) => {
                  const isDirty = dirtyRoles.has(role);
                  const enabledCount = (draftState[role] || []).length;
                  return (
                    <th
                      key={role}
                      className={`${styles.roleHeaderCell} ${isDirty ? styles.roleHeaderDirty : ''}`}
                    >
                      <Tooltip title={role} placement="top" arrow>
                        <Box className={styles.roleHeaderContent}>
                          {isDirty && (
                            <span className={styles.dirtyDot} title="Unsaved changes" />
                          )}
                          <span className={styles.roleLabel}>
                            {formatRoleLabel(role)}
                          </span>
                          <span className={styles.rolePermCount}>
                            {enabledCount}
                          </span>
                        </Box>
                      </Tooltip>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visiblePermissions.map(permission => (
                <tr key={permission} className={styles.matrixRow}>
                  <td className={styles.permissionLabelCell}>
                    <span className={styles.permissionName}>
                      {formatPermissionLabel(permission)}
                    </span>
                    <span className={styles.permissionKey}>{permission}</span>
                  </td>
                  {roles.map(({ role }) => {
                    const isChecked = (draftState[role] || []).includes(
                      permission,
                    );
                    return (
                      <td key={role} className={styles.checkboxCell}>
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={() => togglePermission(role, permission)}
                          sx={{ padding: 0.5 }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </Box>
      </Box>

      {snackbar && (
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          TransitionProps={{ onExited: () => setSnackbar(null) }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      )}
    </Box>
  );
}
